import { PermissionsByGranteeCtorParams } from "permissions/_views/permission-table-view-model";
import PermissionsByGranteeSchema, {
  PermissionsByGranteeJSON,
} from "permissions/by-grantee/_schemas/permissions-by-grantee-schema";
import Permission from "permissions/permission";
import Guid from "_shared/guids/guid";

export default class PermissionsByGrantee {
  public static fromJSON(permissionsJSON: PermissionsByGranteeJSON): PermissionsByGrantee {
    const parsed = PermissionsByGranteeSchema.deserialize(permissionsJSON);

    // The schema decoder already hydrates the preview into Permission models.
    // Re-parsing it here doubled work for every grouped result, including the
    // grouped-permission query's hot path.
    return new PermissionsByGrantee(parsed);
  }

  public static fromJSONArray(jsonArray: PermissionsByGranteeJSON[]): PermissionsByGrantee[] {
    return jsonArray.map((j) => PermissionsByGrantee.fromJSON(j));
  }

  public readonly id: Guid;
  public readonly granteeUserId: Guid;
  public readonly totalPermissions: number;
  public readonly permissionCounts: Record<string, number>;
  public readonly recentPermissionsPreview: Permission[];

  public constructor(params: PermissionsByGranteeCtorParams) {
    this.id = params.id;
    this.granteeUserId = params.granteeUserId;
    this.totalPermissions = params.totalPermissions;
    this.permissionCounts = params.permissionCounts;
    this.recentPermissionsPreview = params.recentPermissionsPreview;
  }

  public toJSON(): PermissionsByGranteeJSON {
    return PermissionsByGranteeSchema.serialize(this);
  }

  public isEqualTo(other: PermissionsByGrantee): boolean {
    if (!other) return false;

    const permissionCountsEqual =
      Object.keys(this.permissionCounts).length === Object.keys(other.permissionCounts).length &&
      Object.entries(this.permissionCounts).every(([key, value]) => other.permissionCounts[key] === value);

    const permissionsPreviewEqual =
      this.recentPermissionsPreview.length === other.recentPermissionsPreview.length &&
      this.recentPermissionsPreview.every((p, index) => p.isEqualTo(other.recentPermissionsPreview[index]));

    return (
      this.id.isEqualTo(other.id) &&
      this.granteeUserId.isEqualTo(other.granteeUserId) &&
      this.totalPermissions === other.totalPermissions &&
      permissionCountsEqual &&
      permissionsPreviewEqual
    );
  }

  public toString(): string {
    return `PermissionsByGrantee { 
      id: ${this.id.value}, 
      granteeUserId: ${this.granteeUserId.value}, 
      totalPermissions: ${this.totalPermissions},
      permissionCounts: ${Object.entries(this.permissionCounts)
        .map(([key, value]) => `${key}: ${value}`)
        .join(", ")},
      recentPermissionsPreview: ${this.recentPermissionsPreview?.map((p) => p.toString()).join(", ") ?? "undefined"},
    }`;
  }
}
